/* eslint-disable max-len */
import { ElementSource, Media, Story } from "chq";
import { ImplementorMontageTemplateParams } from "components/stories/video-studio/utils/creatomate/setMontageCustomTemplate";
import { isStoryElement } from "components/stories/video-studio/utils";
import { getUpdatedTemplateElements, getUpdatedTemplateElementsWithMedia } from "./utils/utils";
import getModifications from "./utils/getModifications";
import { findAllGenericElements } from "../../genericTemplate/utils/findGenericElementByName";

export interface SelectedMedia {
  media: Media;
  story: Story;
}

const genericMontageTemplate = async ({
  preview,
  selectedStories,
  onStoryUpdate,
  organization,
  oldSource
}: ImplementorMontageTemplateParams): Promise<void> => {
  const source = preview.getSource();
  const elements = source?.elements;

  const selectedMedia: SelectedMedia[] = selectedStories.flatMap(
    story => story.visuals?.map(visual => ({ media: visual, story })) || []
  );

  const oldElementsMappings = oldSource.elements.filter((element: ElementSource) => (
    isStoryElement(element)
  )).reduce((acc: ElementSource, element: ElementSource) => ({
    ...acc,
    [element.source]: {
      time: element.time,
      duration: element.duration,
      trim_start: element.trim_start
    }
  }), {});

  const { storyElement: storyElements, introElement: introElements } = findAllGenericElements(elements);
  const sortedIntroElements = [...introElements].sort((a, b) => (a.time + a.duration) - (b.time + b.duration));
  const sortedStoryElements = [...storyElements].sort((a, b) => a.time - b.time);
  const lastStoryElement = sortedStoryElements.reduce((max, current) => (
    current.time > max.time ? current : max
  ), sortedStoryElements[0]);
  const lastIntroElement = sortedIntroElements.reduce((max, current) => (
    (current.time + current.duration) > (max.time + max.duration) ? current : max
  ), sortedIntroElements[0]);
  const storiesEndTime = (lastStoryElement?.time ?? 0) + (lastStoryElement?.duration ?? 0);

  const updatedElements = getUpdatedTemplateElements(elements, selectedMedia, lastIntroElement, organization);
  const elementsWithStoriesTime = getUpdatedTemplateElementsWithMedia(
    updatedElements, onStoryUpdate, selectedMedia, oldElementsMappings, organization
  );

  const { elementMappings, elementsToDelete } = getModifications(
    elementsWithStoriesTime, selectedMedia, updatedElements, organization, storiesEndTime
  );

  const finalElements = elementsWithStoriesTime.filter((element: ElementSource) => !elementsToDelete.includes(element.id)).map((element: ElementSource) => ({
    ...element,
    ...elementMappings[element.id]
  }));

  const lastElement = finalElements.reduce((max, current) => (
    (current.time + current.duration) > (max.time + max.duration) ? current : max
  ), elementsWithStoriesTime[0]);

  const totalDuration = lastElement.time + lastElement.duration;
  await preview.setSource({
    ...source,
    elements: finalElements,
    duration: totalDuration
  }, false);
};

export default genericMontageTemplate;
